import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { usersApi } from '../../services/api';
import Table from '../../components/Table';
import { Search, Plus, Edit2, UserCircle, Check, X, UserX } from 'lucide-react';
import type { User, Role } from '../../types';
import UserModal from './UserModal';

const UserList: React.FC = () => {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(15);
  const [roleFilter, setRoleFilter] = useState<string>('');
  const [activeFilter, setActiveFilter] = useState<string>('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [deactivatingUser, setDeactivatingUser] = useState<User | null>(null);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Fetch roles
  const { data: rolesData } = useQuery({
    queryKey: ['roles'],
    queryFn: async () => {
      const response = await usersApi.getRoles();
      return response.data;
    },
  });

  // Fetch users
  const { data, isLoading } = useQuery({
    queryKey: ['users', debouncedSearch, page, perPage, roleFilter, activeFilter],
    queryFn: async () => {
      const response = await usersApi.getAll({
        search: debouncedSearch || undefined,
        page,
        per_page: perPage,
        role_id: roleFilter ? parseInt(roleFilter) : undefined,
        is_active: activeFilter === '' ? undefined : activeFilter === 'true',
      });
      return response.data;
    },
  });

  // Deactivate mutation
  const deactivateMutation = useMutation({
    mutationFn: (id: number) => usersApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setDeactivatingUser(null);
    },
  });

  const handleEdit = (user: User) => {
    setEditingUser(user);
    setIsModalOpen(true);
  };

  const handleCreate = () => {
    setEditingUser(null);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setEditingUser(null);
  };

  const handleModalSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['users'] });
    handleModalClose();
  };

  const columns = [
    {
      header: 'Utilisateur',
      accessor: (row: User) => (
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
            {row.advisor?.first_name ? (
              <span className="text-primary-700 font-semibold text-sm">
                {row.advisor.first_name.charAt(0).toUpperCase()}
                {row.advisor.last_name?.charAt(0).toUpperCase()}
              </span>
            ) : (
              <UserCircle className="w-6 h-6 text-primary-600" />
            )}
          </div>
          <div>
            <div className="text-sm font-medium text-gray-900">
              {row.advisor?.full_name || row.name}
            </div>
            <div className="text-sm text-gray-500">{row.email}</div>
          </div>
        </div>
      ),
    },
    {
      header: 'Rôle',
      accessor: (row: User) => (
        <span className={`inline-flex px-2.5 py-1 text-xs font-medium rounded-full ${
          row.advisor?.role?.name === 'Admin'
            ? 'bg-purple-100 text-purple-800'
            : row.advisor?.role?.name === 'Finance'
            ? 'bg-blue-100 text-blue-800'
            : row.advisor?.role?.name === 'Workflow'
            ? 'bg-green-100 text-green-800'
            : 'bg-gray-100 text-gray-800'
        }`}>
          {row.advisor?.role?.name || '-'}
        </span>
      ),
    },
    {
      header: 'Statut',
      accessor: (row: User) => (
        <span className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-full ${
          row.advisor?.is_active !== false
            ? 'bg-green-100 text-green-800'
            : 'bg-red-100 text-red-800'
        }`}>
          {row.advisor?.is_active !== false ? (
            <>
              <Check className="w-3 h-3" />
              Actif
            </>
          ) : (
            <>
              <X className="w-3 h-3" />
              Inactif
            </>
          )}
        </span>
      ),
    },
    {
      header: 'Date création',
      accessor: (row: User) => (
        <span className="text-sm text-gray-900">
          {row.created_at ? new Date(row.created_at).toLocaleDateString('fr-FR') : '-'}
        </span>
      ),
    },
    {
      header: 'Actions',
      accessor: (row: User) => (
        <div className="flex items-center gap-2">
          <button
            onClick={() => handleEdit(row)}
            className="p-1.5 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
            title="Modifier"
          >
            <Edit2 className="h-4 w-4" />
          </button>
          {row.advisor?.is_active !== false && (
            <button
              onClick={() => setDeactivatingUser(row)}
              className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              title="Désactiver"
            >
              <UserX className="h-4 w-4" />
            </button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Utilisateurs</h1>
          <p className="text-gray-600 mt-1">Gestion des utilisateurs du backoffice</p>
        </div>
        <button
          onClick={handleCreate}
          className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium"
        >
          <Plus className="w-5 h-5" />
          Nouvel utilisateur
        </button>
      </div>

      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div className="relative md:col-span-2">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Rechercher par nom ou email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
            />
          </div>

          <select
            value={roleFilter}
            onChange={(e) => {
              setRoleFilter(e.target.value);
              setPage(1);
            }}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
          >
            <option value="">Tous les rôles</option>
            {rolesData?.data?.map((role: Role) => (
              <option key={role.id} value={role.id}>
                {role.name}
              </option>
            ))}
          </select>

          <select
            value={activeFilter}
            onChange={(e) => {
              setActiveFilter(e.target.value);
              setPage(1);
            }}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
          >
            <option value="">Tous les statuts</option>
            <option value="true">Actif</option>
            <option value="false">Inactif</option>
          </select>

          <select
            value={perPage}
            onChange={(e) => {
              setPerPage(parseInt(e.target.value));
              setPage(1);
            }}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
          >
            <option value="10">10 par page</option>
            <option value="15">15 par page</option>
            <option value="25">25 par page</option>
            <option value="50">50 par page</option>
          </select>
        </div>
      </div>

      <Table
        data={data?.data || []}
        columns={columns}
        loading={isLoading}
        emptyMessage="Aucun utilisateur trouvé"
        pagination={
          data?.meta
            ? {
                currentPage: data.meta.current_page,
                totalPages: data.meta.last_page,
                total: data.meta.total,
                from: data.meta.from,
                to: data.meta.to,
                onPageChange: setPage,
              }
            : undefined
        }
      />

      {/* User Modal */}
      {isModalOpen && (
        <UserModal
          user={editingUser}
          roles={rolesData?.data || []}
          onClose={handleModalClose}
          onSuccess={handleModalSuccess}
        />
      )}

      {/* Deactivate Confirmation Modal */}
      {deactivatingUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Confirmer la désactivation</h3>
            <p className="text-gray-600 mb-6">
              Êtes-vous sûr de vouloir désactiver l'utilisateur{' '}
              <span className="font-medium">{deactivatingUser.advisor?.full_name || deactivatingUser.name}</span> ?
              L'utilisateur ne pourra plus se connecter au système.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeactivatingUser(null)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={() => deactivateMutation.mutate(deactivatingUser.id)}
                disabled={deactivateMutation.isPending}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                {deactivateMutation.isPending ? 'Désactivation...' : 'Désactiver'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserList;
